/**
 * The review cues follow the engine's reasons.
 *
 * The engine decides whether and why a document waits (ReviewState.Settle stamps
 * `waitReasons`); the line above the fields says the first of them. The screen's
 * own cues (the type cue, field marks, the table cue, the boundary question, F6,
 * the spine and the detached panel's "N to check") are worked out from the
 * fields, rows and findings in hand. They attach to the engine's reasons: they
 * are ordered by them, and a cue for something the engine did not hold the
 * document for is never counted as a thing to check (AP Test 2, 2026-10-13: 15
 * of 40 held documents were not held for their type, yet "Document type not
 * confirmed yet" came first in F6, the spine and the detached panel, and was the
 * only item listed on a document held for its table total).
 *
 * A document saved before the engine named its reasons has none: the screen
 * keeps its own rules for it, unchanged.
 *
 * Words come from the caller (onboarding.ts, WAIT_REASON_WORDS) so this module
 * imports nothing and runs under node as it is.
 */

/** Where on the screen a reason is answered. */
export type WaitCue = 'type' | 'start' | 'field' | 'table' | 'other';

/** The place each engine reason is answered (ReviewState.WaitReason). */
const WAIT_REASON_CUE: Readonly<Record<string, WaitCue>> = {
  'no-type': 'type',
  'type-unconfirmed': 'type',
  'type-confidence': 'type',
  start: 'start',
  'missing-required': 'field',
  'filing-value-missing': 'field',
  'field-confidence': 'field',
  'field-problem': 'field',
  'no-values': 'field',
  arithmetic: 'table',
  'table-total': 'table',
  'line-page': 'table',
};

/** The waiting line's words for each reason (WAIT_REASON_WORDS). */
type Words = Readonly<Record<string, string>>;

type Waiting = {
  needsReview?: boolean;
  confirmedAsWhole?: boolean;
  waitReasons?: readonly string[] | null;
};

/**
 * The engine's reasons this document waits, or null when it named none (a
 * document saved before reasons existed): the screen then keeps its own rules.
 */
export function engineWaitReasons(document: Waiting): readonly string[] | null {
  if (!document.needsReview || document.confirmedAsWhole) return null;
  return document.waitReasons?.length ? document.waitReasons : null;
}

/** Whether the engine holds the document for something answered at `cue`; null when it named no reasons. */
export function engineHoldsAt(waits: readonly string[] | null, cue: WaitCue): boolean | null {
  return waits ? waits.some((reason) => WAIT_REASON_CUE[reason] === cue) : null;
}

const sentence = (words: string) => words.charAt(0).toUpperCase() + words.slice(1);

/**
 * Under "N to check" on the Fields heading: the engine's first reason that no place on the screen
 * answers (you put it back in review, a correction pulled it back, you changed it), which says
 * why it is here rather than what to fix. Every reason with a place says itself there. Null
 * when there is none or the engine named no reasons; not read is told by its own card.
 */
export function waitingNote(document: Waiting, words: Words): string | null {
  const reason = engineWaitReasons(document)?.find(
    (wait) => !WAIT_REASON_CUE[wait] && wait !== 'not-read' && wait !== 'problem',
  );
  return reason && words[reason] ? `${sentence(words[reason])}.` : null;
}

/**
 * The table holds no finding says: the rows that miss the page's subtotal or
 * total (`table-total`), and a row that balances while its printed line says
 * otherwise (`line-page`). Each is said in the waiting line's own words, as one
 * table reason, so the table carries a cue and F6 lands on the line items.
 * (A row that does not add up is already said, row by row, from the rows.)
 */
export function tableWaitSentences(waits: readonly string[] | null, words: Words): string[] {
  return (waits ?? [])
    .filter((reason) => (reason === 'table-total' || reason === 'line-page') && words[reason])
    .map((reason) => sentence(words[reason]));
}

/**
 * What the arithmetic chip must say beside rows that each add up: the engine
 * holds the table for its total or for a line that differs from the page.
 * Null when it does not, or named no reasons.
 */
export function tableHoldWords(
  document: Waiting,
  words: Words,
): { lineDiffersFromPage?: string; total?: string } | null {
  const waits = engineWaitReasons(document);
  if (!waits) return null;
  const lineDiffersFromPage =
    waits.includes('line-page') && words['line-page'] ? sentence(words['line-page']) : undefined;
  const total =
    waits.includes('table-total') && words['table-total']
      ? sentence(words['table-total'])
      : undefined;
  return lineDiffersFromPage || total ? { lineDiffersFromPage, total } : null;
}

/**
 * The cues in the engine's order: each sorts by the first engine reason answered
 * at its place, cues no engine reason backs follow in their own order, and bare
 * cues (which point at nothing to do) come last. With no engine reasons the
 * screen's own order stands: the type first.
 */
export function orderByWaitReasons<T extends { bare?: true }>(
  reasons: readonly T[],
  waits: readonly string[] | null,
  cueOf: (reason: T) => WaitCue,
): T[] {
  if (!waits) {
    const type = reasons.filter((reason) => cueOf(reason) === 'type');
    return [...type, ...reasons.filter((reason) => cueOf(reason) !== 'type')];
  }
  const rank = (reason: T) => {
    if (reason.bare) return waits.length + 1;
    const cue = cueOf(reason);
    const at = waits.findIndex((wait) => WAIT_REASON_CUE[wait] === cue);
    return at < 0 ? waits.length : at;
  };
  return reasons
    .map((reason, index) => ({ reason, index, rank: rank(reason) }))
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map((entry) => entry.reason);
}

/**
 * The things to check: what "N to check" counts and F6 walks, the
 * spine travels to and the detached panel counts. A bare cue puts no mark on the
 * screen, so it is never one: counted, it made "3 to check" over one marked field
 * on a document saved before the engine named its reasons (owner, 2026-10-14).
 */
export function reasonsToCheck<T extends { bare?: true }>(reasons: readonly T[]): T[] {
  return reasons.filter((reason) => !reason.bare);
}
